import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatDate,
  type BadgeColor,
} from '../components/ui';

interface Project {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
}

const statusColor: Record<Project['status'], BadgeColor> = {
  active: 'green',
  completed: 'blue',
  archived: 'gray',
};

const templates = [
  {
    name: 'Client onboarding',
    description: 'Welcome a new client through their first 30 days.',
  },
  {
    name: 'Marketing campaign',
    description: 'Plan, build, and ship a new marketing campaign.',
  },
  {
    name: 'Product launch',
    description: 'Coordinate cross-functional work for a launch.',
  },
];

export function ProjectsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ data: Project[] }>('/projects').catch(() => ({ data: [] })),
  });

  const projects = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle={`${projects.length} projects`}
        action={<Button onClick={() => setShowCreate(true)}>+ New project</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            title="No projects yet"
            description="Group related tasks into projects to keep work organized."
            action={<Button onClick={() => setShowCreate(true)}>+ New project</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
          {projects.map((p) => (
            <Card key={p.id} onClick={() => setEditing(p)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600 }}>{p.name}</h3>
                <Badge color={statusColor[p.status]}>{p.status}</Badge>
              </div>
              {p.description && (
                <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {p.description}
                </p>
              )}
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                Created {formatDate(p.createdAt)}
              </div>
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <ProjectModal
          project={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['projects'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: Project | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [status, setStatus] = useState<Project['status']>(project?.status ?? 'active');
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = { name, description: description || undefined, status };
      return project ? api.patch(`/projects/${project.id}`, body) : api.post('/projects', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/projects/${project!.id}`),
    onSuccess: onSaved,
  });

  return (
    <Modal onClose={onClose} title={project ? 'Edit project' : 'New project'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        {!project && (
          <div>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
              Start from a template
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {templates.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => {
                    setName(t.name);
                    setDescription(t.description);
                  }}
                  style={{
                    padding: 'var(--space-1) var(--space-3)',
                    border: '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-surface-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <Field label="Status">
          <Select
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {project ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
